import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { AlertCircle } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { ContactAddressFormPanel } from '@/components/organisms/contacts/ContactAddressFormPanel';
import { useContact } from '@/hooks/contacts/useContact';
import { useContactAddresses } from '@/hooks/contacts/useContactAddresses';
import type { ContactAddress } from '@/types/contact';

export const Route = createFileRoute(
  '/_dashboard/dashboard/contacts/$contactId/addresses/$addressId/edit',
)({
  component: EditContactAddressPage,
});

function EditContactAddressPage() {
  const { contactId, addressId } = Route.useParams();
  const navigate = useNavigate();
  const contactQuery = useContact(contactId);
  const addressesQuery = useContactAddresses(contactId);
  const addresses: ContactAddress[] = addressesQuery.data ?? [];
  const address = addresses.find((currentAddress) => currentAddress.id === addressId) ?? null;

  function navigateToContact() {
    void navigate({
      to: '/dashboard/contacts/$contactId',
      params: { contactId },
    });
  }

  if (contactQuery.isError && contactQuery.error?.status === 404) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 text-center">
        <AlertCircle className="h-10 w-10 text-muted-foreground" />
        <div>
          <p className="font-semibold">Contact not found</p>
          <p className="text-sm text-muted-foreground">The contact may have been deleted.</p>
        </div>
        <Button onClick={() => navigate({ to: '/dashboard/contacts' })}>Back to Contacts</Button>
      </div>
    );
  }

  if (!addressesQuery.isLoading && !address) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 text-center">
        <AlertCircle className="h-10 w-10 text-muted-foreground" />
        <div>
          <p className="font-semibold">Address not found</p>
          <p className="text-sm text-muted-foreground">It may have been deleted.</p>
        </div>
        <Button onClick={navigateToContact}>Back to Contact</Button>
      </div>
    );
  }

  return (
    <ContactAddressFormPanel
      mode="edit"
      contactId={contactId}
      address={address}
      addressesCount={addresses.length}
      isLoading={contactQuery.isLoading || addressesQuery.isLoading}
      onCancel={navigateToContact}
      onSaved={navigateToContact}
    />
  );
}
